import type {
  ClassicLetterhead,
  ClassicOptions,
  ClassicTemplate,
  ResearchPreviewRow
} from "../../../api/types/classicDrafting";
import type { Draft } from "../../../api/types/draft";

/** Select values for the two choices that have no id. */
export const BUILTIN_TEMPLATE = "__caseblink__";
export const NO_LETTERHEAD = "__none__";

/** What the chooser hands the Case Strategy step. */
export type ClassicPick = {
  templateId: string | null;
  templateName: string;
  letterheadId: string | null;
  letterheadName: string | null;
};

export const templateValue = (t: Pick<ClassicTemplate, "id">): string =>
  t.id ?? BUILTIN_TEMPLATE;

/** The badge on a firm template that can't be picked yet. */
export const notReadyLabel = (reason: string | null): string => {
  const text = (reason ?? "").toLowerCase();
  if (text.includes("review"))
    return text.includes("no ") ? "Not Reviewed" : "In Review";
  if (text.includes("processing failed")) return "Failed";
  if (text.includes("processing")) return "Processing";
  return "Not Ready";
};

export const defaultPick = (options: ClassicOptions): ClassicPick => {
  const template =
    options.templates.find(
      (t) => t.ready && t.id === options.default_template_id
    ) ??
    options.templates.find((t) => t.ready) ??
    null;
  const letterhead =
    options.letterheads.find((l) => l.id === options.default_letterhead_id) ??
    null;
  return {
    templateId: template?.id ?? null,
    templateName: template?.name ?? "CaseBlink",
    letterheadId: letterhead?.id ?? null,
    letterheadName: letterhead?.name ?? null
  };
};

export const letterheadGroups = (
  letterheads: ClassicLetterhead[]
): { yours: ClassicLetterhead[]; firm: ClassicLetterhead[] } => ({
  yours: letterheads.filter((l) => l.mine),
  firm: letterheads.filter((l) => !l.mine)
});

const plural = (n: number, one: string, many = `${one}s`): string =>
  `${n} ${n === 1 ? one : many}`;

export type TopicGroup<T> = {
  key: string;
  title: string;
  rows: T[];
  /** Per-file topics under one heading, rather than one case topic. */
  files: boolean;
};

/**
 * Case topics stand alone and per-file topics gather under their heading
 * ("Journal Impact"), in order of first appearance.
 */
export const groupTopics = <T extends { title: string; group?: string | null }>(
  rows: T[]
): TopicGroup<T>[] => {
  const out: TopicGroup<T>[] = [];
  const byGroup = new Map<string, TopicGroup<T>>();
  rows.forEach((row, index) => {
    if (!row.group) {
      out.push({
        key: `topic-${index}`,
        title: row.title,
        rows: [row],
        files: false
      });
      return;
    }
    const existing = byGroup.get(row.group);
    if (existing) {
      existing.rows.push(row);
      return;
    }
    const group = {
      key: `group-${row.group}`,
      title: row.group,
      rows: [row],
      files: true
    };
    byGroup.set(row.group, group);
    out.push(group);
  });
  return out;
};

/** "Filled 2 answers · 1 suggestion to review." after a whole-case fill. */
export const autofillResult = (
  filled: string[],
  suggested: string[]
): string => {
  const parts = [
    filled.length ? `Filled ${plural(filled.length, "answer")}` : "",
    suggested.length
      ? `${plural(suggested.length, "suggestion")} to review`
      : ""
  ].filter(Boolean);
  return parts.length
    ? `${parts.join(" · ")}.`
    : "Nothing new to fill from case files.";
};

/** "3 files" for a case topic that reads case files, else nothing. */
export const topicFiles = (row: {
  files?: number | null;
}): string | undefined => (row.files ? plural(row.files, "file") : undefined);

/** A per-file topic's file title: its title without the group heading. */
export const topicFileTitle = (row: {
  title: string;
  group?: string | null;
}): string =>
  row.group && row.title.startsWith(`${row.group}: `)
    ? row.title.slice(row.group.length + 2)
    : row.title;

/** "5 topics · reuses 2" for the chooser's Research row. */
export const researchMeta = (rows: ResearchPreviewRow[]): string => {
  const runnable = rows.filter((r) => r.state !== "skipped");
  const reused = runnable.filter((r) => r.state === "ready").length;
  const topics = plural(runnable.length, "topic");
  return reused ? `${topics} · reuses ${reused}` : topics;
};

/** The Research row's sentence: the topics by heading, or why some can't run. */
export const researchSummary = (rows: ResearchPreviewRow[]): string => {
  if (!rows.length) return "No research topics for this template.";
  const blocked = rows.filter((r) => r.state === "blocked");
  if (blocked.length) {
    const reason = blocked[0].reason ?? "More answers needed";
    return `${reason} for ${plural(blocked.length, "topic")}.`;
  }
  const names = groupTopics(rows.filter((r) => r.state !== "skipped")).map(
    (g) => {
      const count = g.files ? g.rows.length : g.rows[0].files;
      return count ? `${g.title} (${plural(count, "file")})` : g.title;
    }
  );
  const skipped = rows.filter((r) => r.state === "skipped");
  const skippedFiles = skipped.filter((r) => r.group).length;
  const skippedTopics = skipped.length - skippedFiles;
  const counts = [
    skippedFiles ? plural(skippedFiles, "file") : "",
    skippedTopics ? plural(skippedTopics, "topic") : ""
  ].filter(Boolean);
  const note = counts.length ? `${counts.join(" and ")} skipped.` : "";
  return [names.length ? `${names.join(", ")}.` : "", note]
    .filter(Boolean)
    .join(" ");
};

/** A Classic run going on this draft (research or writing). */
export const classicRunning = (draft: Draft | undefined): boolean => {
  const progress = draft?.classic_progress;
  return (
    !!progress &&
    !draft?.docx_storage_path &&
    (progress.stage === "researching" || progress.stage === "writing") &&
    draft?.display_status === "running"
  );
};

export const isClassicDraft = (draft: Draft | undefined): boolean =>
  draft?.generation_engine === "legacy";
